import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CancellationCostView } from '@mopla/data-models';
import { formatPriceFromCents } from '@mopla/utils';

import { BookingDetailsDropdown } from '../BookingDetailsDropdown';

interface IBookingCancellationRulesProps {
	rules: CancellationCostView[];
	legLabel?: string;
	testId?: string;
}

const isCompleteRule = (
	rule: CancellationCostView
): rule is Required<CancellationCostView> =>
	rule.costInCent !== undefined && rule.secondsBeforeRideStart !== undefined;

export const BookingCancellationRules: FC<IBookingCancellationRulesProps> = ({
	rules,
	legLabel,
	testId,
}) => {
	const { t } = useTranslation('booking');

	const sortedRules = useMemo(
		() =>
			[...rules]
				.filter(isCompleteRule)
				.sort((a, b) => b.secondsBeforeRideStart - a.secondsBeforeRideStart)
				.map((rule) => {
					const totalMinutes = Math.floor(rule.secondsBeforeRideStart / 60);
					const label =
						totalMinutes >= 60
							? t('cancellation_rules.time_before_hours', {
									hours: Math.floor(totalMinutes / 60),
							  })
							: t('cancellation_rules.time_before', { minutes: totalMinutes });

					return {
						label,
						value:
							rule.costInCent === 0
								? t('cancellation_rules.free')
								: formatPriceFromCents(rule.costInCent),
					};
				}),
		[rules, t]
	);

	return (
		<BookingDetailsDropdown
			title={`${t('cancellation_rules.title')}${
				legLabel ? ` ${legLabel}` : ''
			}`}
			titleVariant="b2-m"
			items={sortedRules}
			itemsGap={1}
			testId={testId}
			toggleTestId="bookingSummary-cancellationDetails-button"
		/>
	);
};
